import { startAiLoadingLines } from '../../../../shared/helpers/ai-loading.helper';
import { renderChatMarkdown } from '../../../../shared/helpers/chat-markdown.helper';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BaseComponent } from '@shared/components/base/base.component';
import { ButtonDirective } from '@shared/components/button/button/button.directive';
import { injectDialogHandle } from '@shared/components/dialog';
import { AssistantChatTurn, AssistantService, AssistantThread } from '../../../../shared/services/local/assistant.service';

/**
 * The global assistant: the email chat's tool loop without an email — tenant-wide questions
 * about mails, Objekte, Kontakte and Dokumente, grounded by the search indices. Opened from
 * the header; the conversation survives closing (session cache in the service).
 */
@Component({
    selector: 'backoffice-assistant-chat-dialog',
    imports: [
        CommonModule,
        TranslateModule,
        ButtonDirective,
    ],
    templateUrl: './assistant-chat-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssistantChatDialogComponent extends BaseComponent {
    // -----------------------------------------------------------------------------------------------------
    // @ DEPENDENCIES
    // -----------------------------------------------------------------------------------------------------

    private readonly assistantService = inject(AssistantService);

    private readonly router = inject(Router);

    private readonly t = inject(TranslateService);

    private readonly dialog = injectDialogHandle();

    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

    // -----------------------------------------------------------------------------------------------------
    // @ INSTANCE VARIABLES
    // -----------------------------------------------------------------------------------------------------

    /** The visible conversation — loaded from the server (persisted per user). */
    readonly messages = signal<AssistantChatTurn[]>([]);

    /** The user's named threads, newest first — claude-style conversations. */
    readonly threads = signal<AssistantThread[]>([]);

    /** The open thread — null: the legacy single history ("Früherer Verlauf"). */
    readonly activeThreadId = signal<string | null>(null);

    /** A fresh, still unsaved thread: created on the FIRST question (title = question). */
    readonly isDraftThread = signal(false);

    readonly isLoading = signal(true);

    readonly isChatting = signal(false);

    /** Starter questions — the empty state is otherwise a blank box nobody knows what to type into. */
    readonly suggestions = [
        'Assistant.Suggestions.OpenReplies',
        'Assistant.Suggestions.MeterReadings',
        'Assistant.Suggestions.LatestInvoice',
    ];

    // -----------------------------------------------------------------------------------------------------
    // @ CONSTRUCTOR
    // -----------------------------------------------------------------------------------------------------

    constructor() {
        super();
        this.assistantService.getThreads().subscribe((threads) => this.threads.set(threads));
        this.loadThread(null);
    }

    /** Switches to a thread (null = legacy history) and loads its turns. */
    loadThread(threadId: string | null): void {
        this.activeThreadId.set(threadId);
        this.isDraftThread.set(false);
        this.isLoading.set(true);
        this.messages.set([]);
        this.assistantService.getHistory(threadId).subscribe({
            next: (turns) => {
                if (this.activeThreadId() === threadId) {
                    this.messages.set(turns);
                    this.isLoading.set(false);
                    this.scrollChat();
                }
            },
            error: () => this.isLoading.set(false),
        });
    }

    /** "+ Neu": empty draft thread — persisted with the first question as its title. */
    newThread(): void {
        this.activeThreadId.set(null);
        this.isDraftThread.set(true);
        this.messages.set([]);
    }

    /** Deletes a thread and falls back to the legacy history view. */
    deleteThread(thread: AssistantThread, event: Event): void {
        event.stopPropagation();
        this.assistantService.deleteThread(thread.id).subscribe(() => {
            this.threads.update((threads) => threads.filter((candidate) => candidate.id !== thread.id));

            if (this.activeThreadId() === thread.id) {
                this.newThread();
            }
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ METHODS
    // -----------------------------------------------------------------------------------------------------

    /** Rotating Hausverwalter one-liners while the agent thinks — claude-style. */
    readonly thinkingLine = signal('');

    private stopThinkingRotation: (() => void) | null = null;

    private startThinking(): void {
        const rotation = startAiLoadingLines();
        this.stopThinkingRotation = rotation.stop;
        // Bridge the helper's signal into ours (effect-free: poll on the same interval)
        this.thinkingLine.set(rotation.line());
        const sync = setInterval(() => this.thinkingLine.set(rotation.line()), 500);
        const stop = this.stopThinkingRotation;
        this.stopThinkingRotation = () => {
            stop();
            clearInterval(sync);
        };
    }

    private stopThinking(): void {
        this.stopThinkingRotation?.();
        this.stopThinkingRotation = null;
    }

    /** Sends one turn — the whole visible conversation travels, the server is stateless. */
    send(input: HTMLInputElement): void {
        const text = input.value.trim();

        if (!text || this.isChatting()) {
            return;
        }

        input.value = '';
        this.messages.update((messages) => [...messages, { role: 'user', content: text }]);
        this.isChatting.set(true);
        this.startThinking();
        this.scrollChat();

        const history = this.messages().map(({ role, content }) => ({ role, content }));

        // A draft thread persists NOW, titled after the first question — then the turn
        // lands inside it (claude-style: the conversation exists once it has content)
        if (this.isDraftThread()) {
            this.assistantService.createThread(text).subscribe((thread) => {
                this.threads.update((threads) => [thread, ...threads]);
                this.activeThreadId.set(thread.id);
                this.isDraftThread.set(false);
                this.chatTurn(history, thread.id);
            });

            return;
        }

        this.chatTurn(history, this.activeThreadId());
    }

    /** One server turn against the given thread (null = legacy history). */
    private chatTurn(history: { role: 'user' | 'assistant'; content: string }[], threadId: string | null): void {
        this.assistantService.chat(history, threadId).subscribe({
            next: (result) => {
                this.stopThinking();
                this.isChatting.set(false);
                this.messages.update((messages) => [
                    ...messages,
                    { role: 'assistant', content: result.reply, sources: result.sources },
                ]);
                this.scrollChat();
            },
            error: () => {
                this.stopThinking();
                this.isChatting.set(false);
                this.messages.update((messages) => [
                    ...messages,
                    { role: 'assistant', content: this.t.instant('Email.Chat.Failed') },
                ]);
            },
        });
    }

    /** Fills a starter question into the input and sends it. */
    ask(input: HTMLInputElement, key: string): void {
        input.value = this.t.instant(key);
        this.send(input);
    }

    /** Forgets the conversation — server and view; a fresh context for an unrelated question. */
    reset(): void {
        this.assistantService.clearHistory(this.activeThreadId()).subscribe(() => this.messages.set([]));
    }

    /** Opens a grounding source in its detail page — the dialog closes, the answer stays cached. */
    openSource(source: { id: string; kind: string }): void {
        const route = this.routeFor(source.kind);

        if (!route) {
            return;
        }

        this.dialog.close();
        this.router.navigate([route, source.id]);
    }

    isSourceClickable(source: { kind: string }): boolean {
        return this.routeFor(source.kind) !== null;
    }

    /**
     * Markdown-light for chat bubbles — same rendering as the email chat: input escaped
     * first, then bold, bullets and headings.
     */
    formatChat(text: string): string {
        return renderChatMarkdown(text);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ PRIVATE METHODS
    // -----------------------------------------------------------------------------------------------------

    private routeFor(kind: string): string | null {
        switch (kind) {
            case 'Email': return '/emails';
            case 'Document': return '/documents';
            case 'Property': return '/properties';
            case 'Contact': return '/contacts';
            default: return null;
        }
    }

    private scrollChat(): void {
        setTimeout(() => {
            const box = this.host.nativeElement.querySelector<HTMLElement>('[data-chat-scroll]');
            if (box) {
                box.scrollTop = box.scrollHeight;
            }
        });
    }
}
